import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Spinner from '../../../components/Spinner';

const fmt = (n, dec = 0) =>
  n == null || isNaN(n) ? '—' : n.toLocaleString('it-IT', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtPct = (n) => n == null || isNaN(n) ? '—' : (n * 100).toFixed(2) + '%';

function calcolaCPAnno(r, annoReale, inailMap) {
  const base = (r.costo_operai || 0) + (r.costo_tecnici || 0) +
    (r.costo_amministrativi || 0) + (r.costo_dirigenti || 0);
  // titolare_soci = numero di titolari/soci → si moltiplica per il valore INAIL dell'anno
  const numTitolari = r.titolare_soci || 0;
  const inailPerPersona = (numTitolari > 0 && annoReale && inailMap?.[annoReale])
    ? inailMap[annoReale] : 0;
  return base + numTitolari * inailPerPersona;
}

function calcolaVARideterminato(VA, Amm, CP) {
  if (!VA || VA <= 0) return VA;
  const rid = [VA];
  if (Amm > 0) { const q = Amm / VA; if (q < 0.02) rid.push(Amm / 0.02); }
  if (CP > 0)  { const p = CP  / VA; if (p < 0.15) rid.push(CP  / 0.15); }
  return Math.min(...rid);
}

function calcolaICPAuto(totVALavori, totVA, totAmm, totAmmStabile, totCP, nSel, impresa) {
  const baseVA = (totVALavori > 0 ? totVALavori : totVA);
  if (!baseVA || baseVA <= 0 || nSel === 0) return null;
  const CA_media          = baseVA / nSel;
  const amm_media         = totAmm / nSel;
  const amm_stabile_media = totAmmStabile / nSel;
  const cp_media          = totCP / nSel;

  const isoOk           = !!impresa?.incremento_iso;
  const patrimonioNetto = impresa?.patrimonio_netto || 0;
  const soglia5         = CA_media * 0.05;
  const soglia10        = CA_media * 0.10;

  const reqA      = patrimonioNetto >= soglia5;
  const reqA_maxi = patrimonioNetto >= soglia10;
  const reqB      = !!impresa?.liquidita_ok;
  const redditiPos = [impresa?.reddito_anno1, impresa?.reddito_anno2, impresa?.reddito_anno3].filter(Boolean).length;
  const reqC      = redditiPos >= 2;

  // Verifica iniziale su VA originale
  const soglia_amm_orig = CA_media * 0.02;
  const reqD_amm_orig   = amm_stabile_media >= soglia_amm_orig * 0.40 && amm_media >= soglia_amm_orig;
  const reqD_cp_orig    = cp_media >= CA_media * 0.15;

  // Se entrambi non verificati: ridetermina VA e ricalcola sul VA rideterminato
  // VA_rid = min(Amm/0.02, CP/0.15) — il parametro con incidenza minore abbassa di più il VA
  let CA_eff = CA_media;
  let va_rid_note = '';
  if (!reqD_amm_orig && !reqD_cp_orig && amm_media > 0 && cp_media > 0) {
    const va_da_amm = amm_media / 0.02;
    const va_da_cp  = cp_media  / 0.15;
    CA_eff = Math.min(va_da_amm, va_da_cp);
    va_rid_note = va_da_cp <= va_da_amm ? 'VA rideterminato su C.P.' : 'VA rideterminato su Amm.';
  }

  const soglia_amm = CA_eff * 0.02;
  const reqD_amm   = amm_stabile_media >= soglia_amm * 0.40 && amm_media >= soglia_amm;
  const reqD_cp    = cp_media >= CA_eff * 0.15;
  const reqD       = reqD_amm && reqD_cp;

  const requisiti = { A: reqA, A_maxi: reqA_maxi, B: reqB, C: reqC, D: reqD, D_amm: reqD_amm, D_cp: reqD_cp, soglia5, soglia10, soglia_amm, patrimonioNetto };

  if (!isoOk) {
    return { icp: 0, icp_max: 0, requisiti, note: 'ISO 9001 non presente' };
  }

  // Base ISO: 10%
  // + componente Amm (se D_amm ok): 0-10% con q tra 0,02 e 0,03
  // + componente CP  (se D_cp ok):  0-10% con p tra 0,15 e 0,225
  const q = Math.min(Math.max(amm_media / CA_eff, 0), 0.03);
  const p = Math.min(Math.max(cp_media  / CA_eff, 0), 0.225);

  let icp = 10;
  if (reqD_amm && q > 0.02) icp += Math.min(((q - 0.02) / 0.01) * 10, 10);
  if (reqD_cp  && p > 0.15) icp += Math.min(((p - 0.15) / 0.075) * 10, 10);

  const maxiIsoOk = reqA_maxi && reqD_amm && reqD_cp && reqB && reqC;
  const icp_max   = maxiIsoOk ? 39 : 30;

  // Bonus maxi ISO: 9% extra proporzionato al minore tra q/0.03 e p/0.225
  // Se entrambi al massimo (q=3%, p=22.5%) → +9% → totale 39%
  if (maxiIsoOk) {
    icp += 9 * Math.min(q / 0.03, p / 0.225);
  }

  icp = Math.max(10, Math.min(icp, icp_max));

  const note = va_rid_note
    ? va_rid_note + (reqD_amm || reqD_cp ? '' : ' — solo base ISO')
    : (!reqD_amm && !reqD_cp ? 'solo base ISO' : (!reqD_amm ? 'solo C.P. verificato' : (!reqD_cp ? 'solo Amm. verificato' : '')));

  return { icp: Math.round(icp * 100) / 100, icp_max, maxi_iso: maxiIsoOk, CA_eff, requisiti, note };
}

export default function TabRiepilogo({ impresaId, impresa }) {
  const [loading, setLoading] = useState(true);
  const [righe, setRighe] = useState([]);
  const [attivi, setAttivi] = useState(new Set());
  const [errore, setErrore] = useState('');
  const printRef = useRef();
  const storageKey = `riepilogo_attivi_${impresaId}`;

  useEffect(() => {
    setLoading(true);
    setErrore('');
    Promise.all([
      axios.get(`/api/dati-economici/cifra-affari/${impresaId}`),
      axios.get(`/api/dati-economici/ammortamenti/${impresaId}`),
      axios.get(`/api/dati-economici/costo-personale/${impresaId}`),
      axios.get('/api/tabella-inail'),
    ]).then(([rCA, rAmm, rCP, rInail]) => {
      const ca    = rCA.data;
      const amm   = rAmm.data;
      const cp    = rCP.data;
      const inailMap = {};
      (rInail.data || []).forEach(t => { inailMap[t.anno] = t.importo; });

      // Per ogni anno, somma tutti i valori (principale + tutte le cedenti)
      const caMain = ca.filter(r => !r.flag_cedente).sort((a, b) => (a.anno || 0) - (b.anno || 0));
      const caByAnno = ca.reduce((acc, r) => {
        const k = r.anno;
        if (!acc[k]) acc[k] = { affari_totale: 0, affari_in_lavori: 0 };
        acc[k].affari_totale    += (r.affari_totale    || 0);
        acc[k].affari_in_lavori += (r.affari_in_lavori || 0);
        return acc;
      }, {});

      function aggAmm(list, posAmm) {
        return list.filter(r => r.anno_pos === posAmm).reduce((acc, r) => ({
          amm_beni_diversi: (acc.amm_beni_diversi||0) + (r.amm_beni_diversi||0),
          amm_attrezzi:     (acc.amm_attrezzi||0)     + (r.amm_attrezzi||0),
          noleggio:         (acc.noleggio||0)          + (r.noleggio||0),
          noleggio_stabile: (acc.noleggio_stabile||0)  + (r.noleggio_stabile||0),
          leasing:          (acc.leasing||0)           + (r.leasing||0),
        }), {});
      }

      const ammMain = amm.filter(r => !r.cedente || r.cedente === 'nessuna');
      const ammCedL = amm.filter(r =>  r.cedente && r.cedente !== 'nessuna');
      const cpMainL = cp.filter(r => !r.cedente || r.cedente === 'nessuna');
      const cpCedL  = cp.filter(r =>  r.cedente && r.cedente !== 'nessuna');

      // Costruisci 15 posizioni
      const rows = Array.from({ length: 15 }, (_, i) => {
        const pos    = i + 1;
        const posAmm = pos;
        const caM    = caMain[i] || null;
        const caSum  = caM?.anno ? (caByAnno[caM.anno] || null) : null;
        const ammR   = aggAmm(ammMain, posAmm);
        const ammCed = aggAmm(ammCedL, posAmm);
        const annoReale = caM?.anno || null;
        const cpR   = cpMainL.filter(r => r.anno_pos === posAmm).reduce((acc, r) => acc + calcolaCPAnno(r, annoReale, inailMap), 0);
        const cpCed = cpCedL.filter(r  => r.anno_pos === posAmm).reduce((acc, r) => acc + calcolaCPAnno(r, annoReale, inailMap), 0);

        const va       = caSum?.affari_in_lavori || 0;
        const vaLavori = caSum?.affari_in_lavori || 0;
        const ammNSLF = (ammR.amm_attrezzi||0) + (ammR.noleggio_stabile||0) + (ammR.leasing||0)
                      + (ammCed.amm_attrezzi||0) + (ammCed.noleggio_stabile||0) + (ammCed.leasing||0);
        const noliOcc = (ammR.noleggio||0) + (ammCed.noleggio||0);
        const totAmm  = ammNSLF + noliOcc;
        const cpTot   = cpR + cpCed;

        return {
          pos,
          anno: caM?.anno || null,
          va,
          vaLavori,
          ammNSLF,
          noliOcc,
          totAmm,
          cp: cpTot,
          hasDati: va > 0 || totAmm > 0 || cpTot > 0,
        };
      });

      setRighe(rows);
      // Ripristina selezione da localStorage, altrimenti seleziona tutti gli anni con dati
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const savedPositions = new Set(JSON.parse(saved));
          const restored = new Set(rows.filter(r => r.hasDati && savedPositions.has(r.pos)).map(r => r.pos));
          setAttivi(restored.size > 0 ? restored : new Set(rows.filter(r => r.hasDati).map(r => r.pos)));
        } else {
          setAttivi(new Set(rows.filter(r => r.hasDati).map(r => r.pos)));
        }
      } catch {
        setAttivi(new Set(rows.filter(r => r.hasDati).map(r => r.pos)));
      }
    }).catch(err => {
      setErrore('Errore nel caricamento: ' + (err.response?.data?.error || err.message));
    }).finally(() => setLoading(false));
  }, [impresaId]);

  function toggleAnno(pos) {
    setAttivi(prev => {
      const next = new Set(prev);
      next.has(pos) ? next.delete(pos) : next.add(pos);
      try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* silenzioso */ }
      return next;
    });
  }

  // Helper: valore effettivo a schermo, 0,00 in stampa per righe deselezionate
  function pv(isAttivo, val, dec = 2) {
    const display = val > 0 ? fmt(val, dec) : '—';
    if (isAttivo) return display;
    return (
      <>
        <span className="print:hidden">{display}</span>
        <span className="hidden print:inline">0,00</span>
      </>
    );
  }
  function pvPct(isAttivo, val) {
    const display = val != null ? fmtPct(val) : '—';
    if (isAttivo) return display;
    return (
      <>
        <span className="print:hidden">{display}</span>
        <span className="hidden print:inline">0,00%</span>
      </>
    );
  }

  // Calcola totali sugli anni selezionati
  const sel         = righe.filter(r => attivi.has(r.pos));
  const nSel        = sel.length;
  const totVA       = sel.reduce((s, r) => s + r.va,        0);
  const totVALavori = sel.reduce((s, r) => s + r.vaLavori,  0);
  const totAmmNSLF  = sel.reduce((s, r) => s + r.ammNSLF,   0);
  const totNoliOcc  = sel.reduce((s, r) => s + r.noliOcc,   0);
  const totAmm      = sel.reduce((s, r) => s + r.totAmm,    0);
  const totCP       = sel.reduce((s, r) => s + r.cp,        0);

  const q = totVA > 0 ? totAmm / totVA : null;
  const p = totVA > 0 ? totCP  / totVA : null;

  // OK se ≥ soglia (il limite superiore è solo un cap nel calcolo, non per la verifica)
  const qOk = q != null && q >= 0.02;
  const pOk = p != null && p >= 0.15;

  const VA_rid = calcolaVARideterminato(totVA, totAmm, totCP);
  const icpCalc = calcolaICPAuto(totVALavori, totVA, totAmm, totAmmNSLF, totCP, nSel, impresa);
  const icpPct  = icpCalc?.icp || 0;
  const VA_inc  = VA_rid * (1 + icpPct / 100);

  function stampa() {
    window.print();
  }

  if (loading) return <Spinner />;
  if (errore)  return <p className="text-red-600 text-sm">{errore}</p>;

  return (
    <div>
      {/* Barra azioni */}
      <div className="flex items-center gap-3 mb-5 print:hidden">
        <button
          onClick={stampa}
          className="ml-auto bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition"
        >
          Stampa / PDF
        </button>
      </div>

      {/* Contenuto stampabile */}
      <div ref={printRef} className="print-area">

        {/* Header riepilogo */}
        <div className="bg-white rounded-xl shadow p-5 mb-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <h3 className="font-bold text-[#1A3A5C] text-base mb-3">Riepilogo dati economici — {impresa?.ragione_sociale}</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500">Volume degli Affari effettivo</p>
                <p className="font-bold text-lg text-[#1A3A5C]">€ {fmt(totVA, 2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">V.A. rideterminato</p>
                <p className={`font-bold text-lg ${VA_rid < totVA ? 'text-orange-600' : 'text-[#1A3A5C]'}`}>€ {fmt(VA_rid, 2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">V.A. incrementato</p>
                <p className="font-bold text-lg text-green-700">€ {fmt(VA_inc, 2)}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm mt-2">
              <div>
                <p className="text-xs text-gray-500">Costo del personale</p>
                <p className="font-semibold">€ {fmt(totCP, 2)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Attrezzature totali</p>
                <p className="font-semibold">€ {fmt(totAmm, 2)}</p>
              </div>
            </div>
          </div>

          {/* Coefficienti + ICP */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <div className={`rounded-lg px-3 py-2 border text-sm ${qOk ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <span className="text-xs text-gray-500 block">q = amm/VA (min 0,02)</span>
                <span className="font-bold">{q != null ? q.toFixed(4) : '—'}</span>
                <span className={`ml-2 text-xs font-bold ${qOk ? 'text-green-600' : 'text-red-600'}`}>{qOk ? '✓ OK' : '✗ NO'}</span>
              </div>
              <div className={`rounded-lg px-3 py-2 border text-sm ${pOk ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <span className="text-xs text-gray-500 block">P = CP/VA (min 15%)</span>
                <span className="font-bold">{p != null ? (p * 100).toFixed(2) + '%' : '—'}</span>
                <span className={`ml-2 text-xs font-bold ${pOk ? 'text-green-600' : 'text-red-600'}`}>{pOk ? '✓ OK' : '✗ NO'}</span>
              </div>
            </div>

            {/* Requisiti ICP */}
            {icpCalc && (
              <div className="text-xs space-y-1">
                {[
                  {
                    key: 'A',
                    label: `Patrimonio netto ≥ 5% CA media (soglia: € ${fmt(icpCalc.requisiti.soglia5, 0)})`,
                    sub: icpCalc.requisiti.A_maxi ? `✓ anche ≥ 10% → coeff. 39` : null,
                    val: icpCalc.requisiti.A,
                  },
                  { key: 'B', label: 'Liquidità verificata (indice ≥ 0,5)', val: icpCalc.requisiti.B },
                  { key: 'C', label: 'Reddito positivo in ≥ 2 degli ultimi 3 anni', val: icpCalc.requisiti.C },
                  { key: 'D', label: `Amm. (stabile ≥ 40% soglia + totale ≥ 2% CA): ${icpCalc.requisiti.D_amm ? '✓' : '✗'}  —  C.P. ≥ 15% CA: ${icpCalc.requisiti.D_cp ? '✓' : '✗'}`, val: icpCalc.requisiti.D },
                ].map(r => (
                  <div key={r.key} className="flex flex-col">
                    <div className="flex items-center gap-1.5">
                      <span className={`font-bold w-3 ${r.val ? 'text-green-600' : 'text-red-500'}`}>{r.val ? '✓' : '✗'}</span>
                      <span className={r.val ? 'text-gray-700' : 'text-gray-400'}>{r.key}) {r.label}</span>
                    </div>
                    {r.sub && <span className="ml-4 text-green-600">{r.sub}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className={`rounded-lg px-3 py-2 border text-sm ${icpPct > 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
              <span className="text-xs text-gray-500 block">ICP calcolato</span>
              <span className={`font-bold text-base ${icpPct > 0 ? 'text-[#1A3A5C]' : 'text-gray-400'}`}>{icpPct > 0 ? icpPct + '%' : 'Non spettante'}</span>
              {icpCalc?.note && <span className="text-xs text-gray-500 ml-2">({icpCalc.note})</span>}
            </div>
          </div>
        </div>

        {/* Tabella anni */}
        <div className="bg-white rounded-xl shadow p-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
              <tr>
                <th className="px-2 py-2 text-center print:hidden">Incl.</th>
                <th className="px-2 py-2 text-left print:hidden">Anno</th>
                <th className="px-2 py-2 text-left">Anno rif.</th>
                <th className="px-3 py-2 text-right">Cifra d'affari €</th>
                <th className="px-3 py-2 text-right">Amm. + NS + L + Fig. €</th>
                <th className="px-3 py-2 text-right">Noli occ. €</th>
                <th className="px-2 py-2 text-right">% Attrezz.</th>
                <th className="px-3 py-2 text-right">Costo personale €</th>
                <th className="px-2 py-2 text-right">% C.P.</th>
              </tr>
            </thead>
            <tbody>
              {righe.map((r, i) => {
                const isAttivo = attivi.has(r.pos);
                const pctAmm = r.va > 0 ? r.totAmm / r.va : null;
                const pctCP  = r.va > 0 ? r.cp    / r.va : null;
                return (
                  <tr
                    key={r.pos}
                    className={`${!r.hasDati ? 'text-gray-300' : isAttivo ? (i % 2 === 0 ? 'bg-white' : 'bg-gray-50') : 'bg-gray-100 text-gray-400 print:opacity-50'}`}
                  >
                    <td className="px-2 py-1.5 text-center print:hidden">
                      <input
                        type="checkbox"
                        checked={isAttivo}
                        onChange={() => toggleAnno(r.pos)}
                        disabled={!r.hasDati}
                        className="accent-[#1A3A5C]"
                      />
                    </td>
                    <td className="px-2 py-1.5 font-semibold text-[#1A3A5C] print:hidden">
                      ANNO{r.pos}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.anno || '—'}
                      {!isAttivo && r.hasDati && <span className="hidden print:inline text-gray-400 font-normal italic"> — escluso</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right">{pv(isAttivo, r.va)}</td>
                    <td className="px-3 py-1.5 text-right">{pv(isAttivo, r.ammNSLF)}</td>
                    <td className="px-3 py-1.5 text-right">{pv(isAttivo, r.noliOcc)}</td>
                    <td className={`px-2 py-1.5 text-right font-medium ${isAttivo && pctAmm != null && (pctAmm < 0.02 || pctAmm > 0.03) ? 'text-orange-500' : ''}`}>
                      {pvPct(isAttivo && r.va > 0, pctAmm)}
                    </td>
                    <td className="px-3 py-1.5 text-right">{pv(isAttivo, r.cp)}</td>
                    <td className={`px-2 py-1.5 text-right font-medium ${isAttivo && pctCP != null && (pctCP < 0.15 || pctCP > 0.225) ? 'text-orange-500' : ''}`}>
                      {pvPct(isAttivo && r.va > 0, pctCP)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#1A3A5C] text-white font-bold">
                <td className="px-2 py-2 print:hidden" />
                <td className="px-2 py-2 print:hidden" />
                <td className="px-2 py-2">TOTALI</td>
                <td className="px-3 py-2 text-right">{fmt(totVA, 2)}</td>
                <td className="px-3 py-2 text-right">{fmt(totAmmNSLF, 2)}</td>
                <td className="px-3 py-2 text-right">{fmt(totNoliOcc, 2)}</td>
                <td className="px-2 py-2 text-right">{q != null ? fmtPct(q) : '—'}</td>
                <td className="px-3 py-2 text-right">{fmt(totCP, 2)}</td>
                <td className="px-2 py-2 text-right">{p != null ? fmtPct(p) : '—'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Stile stampa */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
